/**
 * What the grid's Save sends for each changed row: `id` plus the cells that
 * differ from the row as it was LOADED (ticket #263, 2026-10-27).
 *
 * The old save spread every field of the row, so a row loaded before an
 * agent correction was approved put the old values back on save — and the
 * backend's lock release (a hand edit to a locked column drops the lock)
 * treated that stale copy as a deliberate edit. The backend now also keeps
 * locked fields, but a sparse patch is the right shape regardless: it can't
 * overwrite what someone else changed underneath.
 *
 * No dependencies on purpose — this file is tested from outside the CRA
 * toolchain (see buildPropertyPatches.test.js).
 */

// Keys the grid carries that propertiesMultiUpdate does not accept.
const GRID_ONLY_KEYS = [
  'type', '__typename', 'city', 'neighborhoodId', 'imageThumbUrls', 'imageUrls', 'imageIds',
  'addonItems', 'policyItems', 'updatedAt', 'floorPlans', 'typeDetails', 'feesSheetUrl', 'siteMapUrl',
  'specialsUpdatedAt', 'unitsUpdatedAt', 'displayFloorPlanStatus', 'buildingTypeItems',
];

// Coercions the old save applied to the whole row, now applied per sent cell.
const POSITIVE_NUMBER_KEYS = ['builtYear', 'renovated', 'escort', 'sendEscort', 'units', 'stories', 'cityId'];
const NUMBER_KEYS = ['lat', 'long'];

function coerce(key, value) {
  if (POSITIVE_NUMBER_KEYS.includes(key)) return Number(value) > 0 ? Number(value) : null;
  if (NUMBER_KEYS.includes(key)) return Number(value) ? Number(value) : null;
  return value;
}

function sameValue(a, b) {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((v, i) => sameValue(v, sortedB[i]));
  }
  if (a === b) return true;
  // '' and null both mean "empty" in a grid cell; 125 and '125' are one value.
  if ((a === '' || a == null) && (b === '' || b == null)) return true;
  return String(a) === String(b);
}

export function buildPropertyPatches(changedRows, originalById) {
  const patches = [];
  changedRows.forEach((row) => {
    const original = originalById && originalById[row.id];
    const patch = { id: Number(row.id) };
    Object.keys(row).forEach((key) => {
      if (key === 'id' || GRID_ONLY_KEYS.includes(key)) return;
      if (original && sameValue(row[key], original[key])) return;
      patch[key] = coerce(key, row[key]);
    });
    if (Object.keys(patch).length > 1) patches.push(patch);
  });
  return patches;
}
